import { useEffect, useRef, useState } from 'react';
import { db, doc, getDoc, collection, getDocs } from '../firebase';

// Admin public nicknames (staff_profiles/{uid}.publicName, set by a superAdmin in AdminManagement).
// Every place that shows WHO an admin is goes through here, so once a nickname is set it replaces
// the email / Google display name everywhere — customer pages, chat, admin dashboards.
//
// Reads: one getDoc per uid (rules: public `get`), or ONE list read on staff pages (rules: admins
// may `list`). Cached in memory + localStorage; a cached value is served immediately and refreshed
// in the background once it is older than TTL_MS (stale-while-revalidate).
const TTL_MS = 10 * 60 * 1000;
const LS_KEY = 'fl_staff_names_v1';

const mem = new Map();        // uid -> { name: string|null, at: ms }
const inflight = new Map();   // uid -> Promise
const listeners = new Set();
let listPromise = null;
let version = 0;              // bumped on every notify(); lets a late subscriber catch up

try {
    const saved = JSON.parse(localStorage.getItem(LS_KEY) || '{}');
    Object.entries(saved).forEach(([uid, v]) => { if (v && typeof v.at === 'number') mem.set(uid, v); });
} catch (_) { }

const clean = (s) => (typeof s === 'string' && s.trim() ? s.trim() : null);

function put(uid, name) {
    mem.set(uid, { name: clean(name), at: Date.now() });
}

function notify() {
    version += 1;
    try { localStorage.setItem(LS_KEY, JSON.stringify(Object.fromEntries(mem))); } catch (_) { }
    listeners.forEach(fn => fn());
}

// Cached nickname (any age) or undefined when never fetched. null = fetched, no nickname set.
export function peekStaffName(uid) {
    return uid ? mem.get(uid)?.name : undefined;
}

export async function getStaffName(uid) {
    if (!uid || !db) return null;
    const hit = mem.get(uid);
    if (hit && Date.now() - hit.at < TTL_MS) return hit.name;
    if (inflight.has(uid)) return inflight.get(uid);
    const p = getDoc(doc(db, 'staff_profiles', uid))
        .then(snap => { put(uid, snap.exists() ? snap.data().publicName : null); notify(); return mem.get(uid).name; })
        .catch(() => {
            // Denied / offline: keep any old name and back off for a minute instead of re-reading on every mount.
            mem.set(uid, { name: hit?.name ?? null, at: Date.now() - TTL_MS + 60 * 1000 });
            return hit?.name ?? null;
        })
        .finally(() => inflight.delete(uid));
    inflight.set(uid, p);
    return p;
}

// Staff pages only (rules allow `list` for admins): every nickname in one read per session.
export function loadAllStaffNames() {
    if (!db) return Promise.resolve();
    if (!listPromise) {
        listPromise = getDocs(collection(db, 'staff_profiles'))
            .then(snap => { snap.forEach(d => put(d.id, d.data().publicName)); notify(); })
            .catch(err => { listPromise = null; console.warn('[staffNames] list', err?.message); });
    }
    return listPromise;
}

// Call after a superAdmin edits a nickname so this browser shows it at once.
export function clearStaffNames() {
    mem.clear();
    listPromise = null;
    try { localStorage.removeItem(LS_KEY); } catch (_) { }
    notify();
}

// Display label: the nickname when one is set, else the caller's fallback
// (email local-part / displayName / 'ทีมงาน Fastlane').
export function staffLabel(uid, fallback = 'ทีมงาน Fastlane') {
    return peekStaffName(uid) || fallback;
}

// React hook: re-renders when names arrive and returns staffLabel.
//   useStaffNames({ uids: [order.assignedAdminId] })  → fetch just those (customer pages)
//   useStaffNames({ all: true })                       → one list read (admin pages)
export function useStaffNames({ uids = [], all = false } = {}) {
    const [, setTick] = useState(0);
    const renderedAt = useRef(version);
    useEffect(() => {
        const fn = () => setTick(t => t + 1);
        listeners.add(fn);
        // Names that arrived between the first render and this subscribe would otherwise be missed.
        if (version !== renderedAt.current) fn();
        return () => { listeners.delete(fn); };
    }, []);
    const key = [...new Set(uids.filter(Boolean))].sort().join(',');
    useEffect(() => {
        if (all) loadAllStaffNames();
        else key.split(',').filter(Boolean).forEach(uid => { getStaffName(uid); });
    }, [all, key]);
    return staffLabel;
}
